<template>
    <UserLayout
        :page-title="recommendation?.title || t('recommendationDetails')"
        :page-section="t('aiRecommendations')"
    >
        <div class="ars-wrap">

            <!-- Back -->
            <Link href="/user/ai-recommendations" class="ars-back anim-up" style="--d:0ms">
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                </svg>
                {{ t('backToRecommendations') }}
            </Link>

            <!-- Main card -->
            <div class="ars-card anim-up" style="--d:50ms">
                <div class="ars-card-glow"></div>

                <!-- Header -->
                <div class="ars-hdr">
                    <div class="ars-hdr-left">
                        <div class="ars-hdr-icon">
                            <svg v-if="recommendation.type === 'investment_plan'" width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                            <svg v-else width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                            </svg>
                        </div>
                        <div>
                            <h1 class="ars-title">{{ recommendation.title }}</h1>
                            <p class="ars-gen-date">{{ t('generatedOn') }} {{ formatDate(recommendation.created_at) }}</p>
                        </div>
                    </div>
                    <div class="ars-hdr-badges">
                        <span class="ars-status-badge" :class="getStatusBadgeClass(recommendation.status)">
                            {{ recommendation.status }}
                        </span>
                        <span class="ars-priority-badge" :class="getPriorityBadgeClass(recommendation.priority)">
                            {{ recommendation.priority }} {{ t('priority') }}
                        </span>
                    </div>
                </div>

                <!-- Confidence bar -->
                <div class="ars-confidence">
                    <div class="ars-confidence-top">
                        <span class="ars-confidence-label">{{ t('confidenceScore') }}</span>
                        <span class="ars-confidence-val">{{ recommendation.confidence_score }}%</span>
                    </div>
                    <div class="ars-confidence-track">
                        <div class="ars-confidence-fill" :style="`width:${recommendation.confidence_score}%`"></div>
                    </div>
                    <p class="ars-confidence-text">{{ getConfidenceLabel(recommendation.confidence_score) }}</p>
                </div>

                <!-- Description -->
                <div class="ars-section">
                    <div class="ars-section-title">
                        <span class="ars-section-dot"></span>
                        {{ t('description') }}
                    </div>
                    <p class="ars-desc">{{ recommendation.description }}</p>
                </div>

                <!-- Investment plan (if applicable) -->
                <div v-if="recommendation.type === 'investment_plan' && investmentPlan" class="ars-section">
                    <div class="ars-section-title">
                        <span class="ars-section-dot"></span>
                        {{ t('recommendedPlan') }}
                    </div>
                    <div class="ars-plan">
                        <div class="ars-plan-top">
                            <h3 class="ars-plan-name">{{ investmentPlan.name }}</h3>
                            <span v-if="investmentPlan.featured" class="ars-featured-tag">{{ t('featured') }}</span>
                        </div>
                        <p class="ars-plan-desc">{{ investmentPlan.description }}</p>
                        <div class="ars-plan-stats">
                            <div class="ars-plan-stat">
                                <span class="ars-plan-stat-label">{{ t('interestRate') }}</span>
                                <span class="ars-plan-stat-val ars-plan-stat-val--purple">{{ investmentPlan.interest_rate }}%</span>
                                <span class="ars-plan-stat-sub">{{ investmentPlan.return_type }}</span>
                            </div>
                            <div class="ars-plan-stat">
                                <span class="ars-plan-stat-label">{{ t('duration') }}</span>
                                <span class="ars-plan-stat-val">{{ investmentPlan.duration_type === 'lifetime' ? (t('lifetime') || 'Lifetime') : investmentPlan.duration_days }}</span>
                                <span class="ars-plan-stat-sub">{{ investmentPlan.duration_type === 'lifetime' ? (t('untilProfitCap') || 'until profit cap') : t('days') }}</span>
                            </div>
                            <div class="ars-plan-stat">
                                <span class="ars-plan-stat-label">{{ t('minInvestment') }}</span>
                                <span class="ars-plan-stat-val">{{ currencySymbol }}{{ formatCurrency(investmentPlan.min_amount) }}</span>
                            </div>
                            <div class="ars-plan-stat">
                                <span class="ars-plan-stat-label">{{ t('riskLevel') }}</span>
                                <span class="ars-plan-stat-val" :class="getRiskLevelClass(investmentPlan.risk_level)">{{ investmentPlan.risk_level }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Key insights -->
                <div class="ars-section">
                    <div class="ars-section-title">
                        <span class="ars-section-dot"></span>
                        {{ t('keyInsights') }}
                    </div>
                    <div class="ars-insights-grid">
                        <div v-if="recommendation.recommendation_data.suggested_amount" class="ars-insight">
                            <div class="ars-insight-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <h4 class="ars-insight-title">{{ t('suggestedAmount') }}</h4>
                            <p class="ars-insight-val">{{ currencySymbol }}{{ formatCurrency(recommendation.recommendation_data.suggested_amount) }}</p>
                            <p class="ars-insight-sub">{{ t('basedOnYourPortfolio') }}</p>
                        </div>
                        <div v-if="recommendation.recommendation_data.expected_roi" class="ars-insight">
                            <div class="ars-insight-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                                </svg>
                            </div>
                            <h4 class="ars-insight-title">{{ t('expectedROI') }}</h4>
                            <p class="ars-insight-val">{{ recommendation.recommendation_data.expected_roi }}%</p>
                            <p class="ars-insight-sub">{{ t('projectedReturn') }}</p>
                        </div>
                        <div v-if="recommendation.recommendation_data.recent_earnings" class="ars-insight">
                            <div class="ars-insight-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 8h6m-5 0a3 3 0 110 6H9l3 3m-3-6h6m6 1a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </div>
                            <h4 class="ars-insight-title">{{ t('recentEarnings') }}</h4>
                            <p class="ars-insight-val">{{ currencySymbol }}{{ formatCurrency(recommendation.recommendation_data.recent_earnings) }}</p>
                            <p class="ars-insight-sub">{{ t('last30Days') }}</p>
                        </div>
                        <div v-if="recommendation.recommendation_data.duration_days || recommendation.recommendation_data.duration_type === 'lifetime'" class="ars-insight">
                            <div class="ars-insight-icon">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                                </svg>
                            </div>
                            <h4 class="ars-insight-title">{{ t('investmentPeriod') }}</h4>
                            <p class="ars-insight-val">{{ recommendation.recommendation_data.duration_type === 'lifetime' ? (t('lifetime') || 'Lifetime') : recommendation.recommendation_data.duration_days }}</p>
                            <p class="ars-insight-sub">{{ recommendation.recommendation_data.duration_type === 'lifetime' ? (t('untilProfitCap') || 'until profit cap') : t('days') }}</p>
                        </div>
                    </div>

                    <div v-if="recommendation.recommendation_data.reason" class="ars-reason">
                        <div class="ars-reason-head">
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            {{ t('whyThisRecommendation') }}
                        </div>
                        <p class="ars-reason-text">{{ recommendation.recommendation_data.reason }}</p>
                    </div>
                </div>

                <!-- Actions -->
                <div class="ars-actions">
                    <button v-if="recommendation.status === 'active' && recommendation.type === 'investment_plan'"
                        @click="applyRecommendation" class="ars-btn-apply">
                        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        {{ t('applyRecommendation') }}
                    </button>
                    <button v-if="recommendation.status === 'active'"
                        @click="dismissRecommendation" class="ars-btn-ghost">
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                        {{ t('dismiss') }}
                    </button>
                    <Link href="/user/ai-recommendations" class="ars-btn-ghost">
                        {{ t('backToList') }}
                    </Link>
                </div>
            </div>

            <!-- How AI works -->
            <div class="ars-card anim-up" style="--d:120ms">
                <div class="ars-card-glow"></div>
                <div class="ars-how-hdr">
                    <div class="ars-how-icon">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                        </svg>
                    </div>
                    <div>
                        <h2 class="ars-how-title">{{ t('howAiWorks') }}</h2>
                        <p class="ars-how-sub">{{ t('aiAnalyzesYourPortfolio') }}</p>
                    </div>
                </div>
                <div class="ars-how-grid">
                    <div class="ars-how-item">
                        <div class="ars-how-item-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                            </svg>
                        </div>
                        <h3 class="ars-how-item-title">{{ t('dataAnalysis') }}</h3>
                        <p class="ars-how-item-text">{{ t('analyzesHistoricalData') }}</p>
                    </div>
                    <div class="ars-how-item">
                        <div class="ars-how-item-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"/>
                            </svg>
                        </div>
                        <h3 class="ars-how-item-title">{{ t('riskMatching') }}</h3>
                        <p class="ars-how-item-text">{{ t('matchesYourRiskProfile') }}</p>
                    </div>
                    <div class="ars-how-item">
                        <div class="ars-how-item-icon">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                            </svg>
                        </div>
                        <h3 class="ars-how-item-title">{{ t('realTimeUpdates') }}</h3>
                        <p class="ars-how-item-text">{{ t('continuouslyUpdated') }}</p>
                    </div>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<script setup>
import { computed } from 'vue';
import { usePage, Link, router } from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import { useToast } from "@/composables/useToast.js";
import { useTranslation } from '@/composables/useTranslation';

const props = defineProps({
    recommendation: {
        type: Object,
        required: true
    },
    investmentPlan: {
        type: Object,
        default: null
    }
});

const { showToast } = useToast();
const { t } = useTranslation();
const page = usePage();

const primaryColor = computed(() => page.props.primaryColor || '#86efac');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 134, g: 239, b: 172 };
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return "#" + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const applyRecommendation = () => {
    router.post(`/user/ai-recommendations/${props.recommendation.id}/apply`, {}, {
        onSuccess: () => {
        },
        onError: () => {
            showToast(t('failedToApply') || 'Failed to apply recommendation', 'error');
        }
    });
};

const dismissRecommendation = () => {
    router.post(`/user/ai-recommendations/${props.recommendation.id}/dismiss`, {}, {
        onSuccess: () => {
            showToast(t('recommendationDismissed') || 'Recommendation dismissed', 'success');
        },
        onError: () => {
            showToast(t('failedToDismiss') || 'Failed to dismiss recommendation', 'error');
        }
    });
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(number);
};

const getStatusBadgeClass = (status) => {
    const classes = {
        active: 'ars-badge--green',
        dismissed: 'ars-badge--slate',
        applied: 'ars-badge--blue'
    };
    return classes[status] || 'ars-badge--slate';
};

const getPriorityBadgeClass = (priority) => {
    const classes = {
        high: 'ars-badge--red',
        medium: 'ars-badge--amber',
        low: 'ars-badge--slate'
    };
    return classes[priority] || 'ars-badge--slate';
};

const getRiskLevelClass = (risk) => {
    const classes = {
        low: 'ars-risk--low',
        medium: 'ars-risk--medium',
        high: 'ars-risk--high'
    };
    return classes[risk] || 'ars-risk--low';
};

const getConfidenceLabel = (score) => {
    if (score >= 85) return t('veryHighConfidence') || 'Very High Confidence';
    if (score >= 70) return t('highConfidence') || 'High Confidence';
    if (score >= 55) return t('moderateConfidence') || 'Moderate Confidence';
    return t('lowConfidence') || 'Low Confidence';
};
</script>

<style scoped>
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

.ars-wrap { width: 100%; padding: 24px 16px; box-sizing: border-box; display: flex; flex-direction: column; gap: 20px; max-width: 900px; margin: 0 auto; }
@media(min-width:640px){ .ars-wrap{ padding: 32px 24px; } }
@media(min-width:1024px){ .ars-wrap{ padding: 40px 32px; } }

.ars-back {
    display: inline-flex; align-items: center; gap: 6px;
    color: rgba(255,255,255,0.4); font-size: 13px; font-weight: 600;
    text-decoration: none; transition: color 0.2s; align-self: flex-start;
}
.ars-back:hover { color: #a75ded; }

.ars-card {
    background: rgba(20,10,40,0.92); border: 1px solid rgba(167,93,237,0.18);
    border-radius: 18px; overflow: hidden; position: relative;
}
.ars-card::before { content: ''; position: absolute; inset: 0 0 auto 0; height: 1px; background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent); }
.ars-card-glow { position: absolute; top: -60px; right: -60px; width: 200px; height: 200px; background: radial-gradient(circle,rgba(167,93,237,0.06) 0%,transparent 70%); pointer-events: none; }

/* header */
.ars-hdr { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 16px; padding: 24px 24px 0; }
.ars-hdr-left { display: flex; align-items: flex-start; gap: 14px; flex: 1; min-width: 0; }
.ars-hdr-icon { width: 56px; height: 56px; border-radius: 14px; background: linear-gradient(135deg,rgba(167,93,237,0.25),rgba(167,93,237,0.08)); border: 1px solid rgba(167,93,237,0.25); display: flex; align-items: center; justify-content: center; color: #a75ded; flex-shrink: 0; }
.ars-title { font-size: 20px; font-weight: 800; color: #fff; margin-bottom: 4px; }
.ars-gen-date { font-size: 12px; color: rgba(255,255,255,0.35); }
.ars-hdr-badges { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; flex-shrink: 0; }
.ars-status-badge, .ars-priority-badge { padding: 5px 12px; border-radius: 8px; font-size: 11px; font-weight: 700; text-transform: capitalize; }
.ars-badge--green { background: rgba(34,197,94,0.12); color: #4ade80; }
.ars-badge--blue  { background: rgba(59,130,246,0.12); color: #60a5fa; }
.ars-badge--slate { background: rgba(100,116,139,0.15); color: #94a3b8; }
.ars-badge--red   { background: rgba(239,68,68,0.12); color: #f87171; }
.ars-badge--amber { background: rgba(245,158,11,0.12); color: #fbbf24; }

/* confidence */
.ars-confidence { padding: 20px 24px; border-bottom: 1px solid rgba(167,93,237,0.08); }
.ars-confidence-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.ars-confidence-label { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.7); }
.ars-confidence-val { font-size: 22px; font-weight: 800; color: #a75ded; }
.ars-confidence-track { width: 100%; height: 10px; background: rgba(167,93,237,0.1); border-radius: 5px; overflow: hidden; }
.ars-confidence-fill { height: 100%; background: linear-gradient(90deg,#a75ded,#7c3aed); border-radius: 5px; transition: width 1s ease; }
.ars-confidence-text { font-size: 12px; color: rgba(255,255,255,0.4); margin-top: 6px; }

/* section */
.ars-section { padding: 20px 24px; border-bottom: 1px solid rgba(167,93,237,0.06); }
.ars-section:last-of-type { border-bottom: none; }
.ars-section-title { display: flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 700; color: #fff; margin-bottom: 14px; }
.ars-section-dot { width: 8px; height: 8px; border-radius: 50%; background: #a75ded; flex-shrink: 0; }
.ars-desc { font-size: 14px; color: rgba(255,255,255,0.65); line-height: 1.7; }

/* plan */
.ars-plan { background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.12); border-radius: 14px; padding: 18px 20px; }
.ars-plan-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.ars-plan-name { font-size: 18px; font-weight: 800; color: #fff; }
.ars-featured-tag { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; background: rgba(167,93,237,0.15); color: #a75ded; }
.ars-plan-desc { font-size: 13px; color: rgba(255,255,255,0.5); margin-bottom: 16px; line-height: 1.5; }
.ars-plan-stats { display: grid; grid-template-columns: repeat(2,1fr); gap: 10px; }
@media(min-width:640px){ .ars-plan-stats{ grid-template-columns: repeat(4,1fr); } }
.ars-plan-stat { background: rgba(10,4,22,0.7); border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 3px; }
.ars-plan-stat-label { font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.35); text-transform: uppercase; letter-spacing: 0.06em; }
.ars-plan-stat-val { font-size: 18px; font-weight: 800; color: #fff; text-transform: capitalize; }
.ars-plan-stat-val--purple { color: #a75ded; }
.ars-plan-stat-sub { font-size: 10px; color: rgba(255,255,255,0.35); }
.ars-risk--low { color: #4ade80; }
.ars-risk--medium { color: #fbbf24; }
.ars-risk--high { color: #f87171; }

/* insights */
.ars-insights-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
@media(min-width:640px){ .ars-insights-grid{ grid-template-columns: repeat(2,1fr); } }
.ars-insight {
    padding: 16px; border-radius: 12px;
    background: linear-gradient(135deg,rgba(167,93,237,0.1),rgba(167,93,237,0.04));
    border: 1px solid rgba(167,93,237,0.2);
}
.ars-insight-icon { width: 34px; height: 34px; border-radius: 8px; background: rgba(167,93,237,0.12); display: flex; align-items: center; justify-content: center; color: #a75ded; margin-bottom: 10px; }
.ars-insight-title { font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.7); margin-bottom: 6px; }
.ars-insight-val { font-size: 20px; font-weight: 800; color: #fff; margin-bottom: 3px; }
.ars-insight-sub { font-size: 11px; color: rgba(255,255,255,0.4); }

.ars-reason { background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.1); border-radius: 12px; padding: 16px; }
.ars-reason-head { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 10px; color: #a75ded; }
.ars-reason-text { font-size: 13px; color: rgba(255,255,255,0.6); line-height: 1.6; }

/* actions */
.ars-actions { display: flex; flex-wrap: wrap; gap: 10px; padding: 20px 24px; background: rgba(10,4,22,0.4); border-top: 1px solid rgba(167,93,237,0.08); }
.ars-btn-apply {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 22px; border-radius: 10px; border: none; cursor: pointer;
    background: linear-gradient(135deg,#a75ded,#7c3aed); color: #fff; font-size: 14px; font-weight: 700;
    transition: opacity 0.2s, transform 0.15s;
}
.ars-btn-apply:hover { opacity: 0.9; transform: translateY(-1px); }
.ars-btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 22px; border-radius: 10px;
    background: rgba(10,4,22,0.7); border: 1px solid rgba(167,93,237,0.15);
    color: rgba(255,255,255,0.5); font-size: 14px; font-weight: 600;
    cursor: pointer; text-decoration: none; transition: color 0.2s, background 0.2s;
}
.ars-btn-ghost:hover { color: #fff; background: rgba(167,93,237,0.08); }

/* how AI works */
.ars-how-hdr { display: flex; align-items: flex-start; gap: 14px; padding: 22px 24px 16px; border-bottom: 1px solid rgba(167,93,237,0.08); }
.ars-how-icon { width: 44px; height: 44px; border-radius: 12px; background: rgba(167,93,237,0.12); border: 1px solid rgba(167,93,237,0.2); display: flex; align-items: center; justify-content: center; color: #a75ded; flex-shrink: 0; }
.ars-how-title { font-size: 15px; font-weight: 700; color: #fff; margin-bottom: 4px; }
.ars-how-sub { font-size: 12px; color: rgba(255,255,255,0.45); line-height: 1.5; }
.ars-how-grid { display: grid; grid-template-columns: 1fr; gap: 12px; padding: 20px 24px; }
@media(min-width:640px){ .ars-how-grid{ grid-template-columns: repeat(3,1fr); } }
.ars-how-item { padding: 16px; background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.1); border-radius: 12px; }
.ars-how-item-icon { width: 36px; height: 36px; border-radius: 8px; background: rgba(167,93,237,0.1); display: flex; align-items: center; justify-content: center; color: #a75ded; margin-bottom: 10px; }
.ars-how-item-title { font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 6px; }
.ars-how-item-text { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.5; }
</style>
